/**
 * Chart documentation shown behind the info icon on the Devices dashboard.
 *
 * Keys match the chart and table link property names in `MopaDevicesComponent`.
 */
export const MopaDevicesChartDocs = {
    revenue: /* HTML */ `<p>
            The 200 highest-earning device models in the selected date or
            period.
        </p>
        <p>
            <strong>Columns:</strong> device brand and model;
            <strong>Revenue net pln</strong> - net revenue summed over the
            period. Ranked by net PLN, cut at 200 rows.
        </p>
        <p>
            Ignores the <strong>Selected device model</strong> filter; the
            <strong>Device model</strong> filter still applies.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device,
            not the one used that day; valid players only.
        </p>`,

    users: /* HTML */ `<p>
            The 200 most common device models in the selected date or period.
        </p>
        <p>
            <strong>Columns:</strong> device brand and model;
            <strong>Users</strong> - distinct <code>player_id</code>. Ranked by
            users, cut at 200 rows.
        </p>
        <p>
            Ignores the <strong>Selected device model</strong> filter; the
            <strong>Device model</strong> filter still applies.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device,
            not the one used that day; valid players only.
        </p>`,

    selected_devices: /* HTML */ `<p>
            Users and revenue of the models named in the
            <strong>Selected device model</strong> filter, in the selected date
            or period.
        </p>
        <p>
            <strong>Columns:</strong> device category, brand and model;
            <strong>Users</strong> - distinct <code>player_id</code>;
            <strong>Revenue net pln</strong> - net revenue summed over the
            period. No row limit; sorted by users.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device,
            not the one used that day; valid players only.
        </p>`,

    device_revenue_country: /* HTML */ `<p>
            Revenue of the models named in the
            <strong>Selected device model</strong> filter, split by country, in
            the selected date or period.
        </p>
        <p>
            <strong>Columns:</strong> <strong>Country</strong> - the player's
            first country; <strong>Revenue net pln</strong> - net revenue summed
            over the period. No row limit; sorted by revenue.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device
            and first country, not the ones used that day; valid players only.
        </p>`,

    selected_device_daily_revenue: /* HTML */ `<p>
            Revenue from players whose first device is named in the
            <strong>Selected device model</strong> filter, per day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net revenue in PLN.
        </p>
        <p>
            <strong>What's counted:</strong> revenue summed per date. Always net
            and in PLN - this dashboard has no net/gross or currency toggle.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device,
            not the one used that day; valid players only.
        </p>`,

    selected_device_daily_revenue_by_country: /* HTML */ `<p>
            Revenue of the models named in the
            <strong>Selected device model</strong> filter, per date, split into
            countries.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net revenue in PLN, one
            line per country.
        </p>
        <p>
            <strong>What's counted:</strong> revenue summed per date and
            country, net and in PLN; the top 20 countries keep their own line,
            the rest are merged into <em>Other</em>.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device
            and first country, not the ones used that day; valid players only.
        </p>`,

    selected_device_daily_users: /* HTML */ `<p>
            Players whose first device is named in the
            <strong>Selected device model</strong> filter, per day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - players.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> per
            date; a player counts once a day however many sessions they play.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device,
            not the one used that day; valid players only.
        </p>`,

    selected_devices_daily_users_by_device_model: /* HTML */ `<p>
            Players of the models named in the
            <strong>Selected device model</strong> filter, per date, one line
            per device model.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - players, one line per
            device model.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> per
            date and model; a player belongs to exactly one model, their first
            device.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; first device,
            not the one used that day; valid players only.
        </p>`,
} as const;
